import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';

/**
 * Sites the signed-in person can't open because the site's plan pauses team
 * members over its limit (e.g. after it dropped to Free). Their access comes
 * back by itself when the site's owner upgrades. With no other site, this is
 * the whole page; otherwise a note above the current one.
 */

const PAUSED_MEMBERSHIPS = gql`
  query PausedMemberships {
    pausedMemberships {
      accountId
      name
    }
  }
`;

export function usePausedMemberships() {
  const { data } = useQuery<{ pausedMemberships: Array<{ accountId: string; name: string }> }>(
    PAUSED_MEMBERSHIPS,
    { fetchPolicy: 'cache-and-network' }
  );
  return data?.pausedMemberships || [];
}

const names = (list: Array<{ name: string }>) =>
  list.length === 1
    ? `“${list[0].name}”`
    : `${list
        .slice(0, -1)
        .map((s) => `“${s.name}”`)
        .join(', ')} and “${list[list.length - 1].name}”`;

export function PausedAccess({ children }: { children: React.ReactNode }) {
  const { accounts, isLoading } = useAccount();
  const paused = usePausedMemberships();
  if (!paused.length) return <>{children}</>;

  const message = `Your access to ${names(paused)} is paused. ${
    paused.length === 1 ? 'Its' : 'Their'
  } plan includes fewer team members than ${paused.length === 1 ? 'it has' : 'they have'}. It comes back when the site’s owner upgrades.`;

  if (!isLoading && accounts.length === 0) {
    return (
      <div className="ink-paused-page">
        <div className="ink-paused-card">
          <div className="ink-paused-title">Your access is paused</div>
          <p>{message}</p>
          <p className="ink-muted">Nothing on the site was removed.</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <p className="ink-paused-note">{message}</p>
      {children}
    </>
  );
}
